import React, { useEffect, useState } from 'react';
import { ref, get, set } from 'firebase/database';
import { rtdb } from '../../../firebase';
import { Plus, Trash2, CheckCircle2, AlertCircle, UtensilsCrossed } from 'lucide-react';

const EMPTY_CATEGORY = { libelle: '', plats: [{ libelle: '' }] };

const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function MenuPanel({ crousId }) {
  const [period, setPeriod] = useState('midi');
  const [categories, setCategories] = useState([{ ...EMPTY_CATEGORY, plats: [{ libelle: '' }] }]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [dateKey, setDateKey] = useState(getTodayKey());
  const [existingSource, setExistingSource] = useState(null);

  useEffect(() => {
    loadExistingMenu();
  }, [crousId, dateKey, period]);

  const loadExistingMenu = async () => {
    try {
      const menuRef = ref(rtdb, `crous_menus/${crousId}/${dateKey}`);
      const snap = await get(menuRef);
      if (snap.exists()) {
        const data = snap.val();
        const periodData = data[period];
        if (periodData && periodData.categories) {
          setCategories(periodData.categories.map(c => ({
            libelle: c.libelle || '',
            plats: (c.plats || []).map(p => ({ libelle: p.libelle || '' }))
          })));
          setExistingSource('crous');
          return;
        }
      }
      setExistingSource(null);
    } catch (e) {
      console.warn('MenuPanel: load failed', e);
    }
  };

  const addCategory = () => {
    setCategories([...categories, { libelle: '', plats: [{ libelle: '' }] }]);
  };

  const removeCategory = (idx) => {
    setCategories(categories.filter((_, i) => i !== idx));
  };

  const updateCategoryName = (idx, value) => {
    const updated = [...categories];
    updated[idx] = { ...updated[idx], libelle: value };
    setCategories(updated);
  };

  const addPlat = (catIdx) => {
    const updated = [...categories];
    updated[catIdx] = {
      ...updated[catIdx],
      plats: [...updated[catIdx].plats, { libelle: '' }]
    };
    setCategories(updated);
  };

  const removePlat = (catIdx, platIdx) => {
    const updated = [...categories];
    updated[catIdx] = {
      ...updated[catIdx],
      plats: updated[catIdx].plats.filter((_, i) => i !== platIdx)
    };
    setCategories(updated);
  };

  const updatePlat = (catIdx, platIdx, value) => {
    const updated = [...categories];
    const plats = [...updated[catIdx].plats];
    plats[platIdx] = { libelle: value };
    updated[catIdx] = { ...updated[catIdx], plats };
    setCategories(updated);
  };

  const handleSubmit = async () => {
    const cleaned = categories
      .filter(c => c.libelle.trim())
      .map((c, ci) => ({
        code: `cat_${ci}`,
        libelle: c.libelle.trim(),
        plats: c.plats
          .filter(p => p.libelle.trim())
          .map((p, pi) => ({ code: `p_${ci}_${pi}`, libelle: p.libelle.trim() }))
      }))
      .filter(c => c.plats.length > 0);

    if (cleaned.length === 0) {
      setFeedback({ kind: 'err', text: 'Ajoute au moins une catégorie avec un plat.' });
      return;
    }

    setSubmitting(true);
    setFeedback(null);

    try {
      const menuRef = ref(rtdb, `crous_menus/${crousId}/${dateKey}/${period}`);
      await set(menuRef, {
        categories: cleaned,
        updatedAt: Date.now(),
      });
      setFeedback({ kind: 'ok', text: `Menu ${period} enregistré pour le ${dateKey}. Visible immédiatement.` });
      setExistingSource('crous');
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async () => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const menuRef = ref(rtdb, `crous_menus/${crousId}/${dateKey}/${period}`);
      await set(menuRef, null);
      setCategories([{ libelle: '', plats: [{ libelle: '' }] }]);
      setExistingSource(null);
      setFeedback({ kind: 'ok', text: 'Menu supprimé. L\'API CROUStillant reprendra le relais.' });
    } catch (err) {
      setFeedback({ kind: 'err', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="est-card">
      <div className="est-card-head">
        <div>
          <span className="est-kicker">Personnalisation</span>
          <h2>Menu du restaurant</h2>
          <p className="est-muted">
            Le menu que tu saisis ici sera affiché en priorité aux étudiants.
            Sans saisie, le menu provient de l'API CROUStillant.
          </p>
        </div>
        {existingSource === 'crous' && (
          <div className="est-badge est-badge-active">
            Menu personnalisé actif
          </div>
        )}
      </div>

      {/* Date + Period selector */}
      <div className="est-menu-controls">
        <label className="est-field" style={{ flex: 1 }}>
          <span>Date</span>
          <input
            type="date"
            value={dateKey}
            onChange={(e) => setDateKey(e.target.value)}
          />
        </label>
        <div className="est-duration">
          <span className="est-muted">Service</span>
          <div className="est-duration-chips">
            <button
              type="button"
              className={`est-chip ${period === 'midi' ? 'is-selected' : ''}`}
              onClick={() => setPeriod('midi')}
            >
              Midi
            </button>
            <button
              type="button"
              className={`est-chip ${period === 'soir' ? 'is-selected' : ''}`}
              onClick={() => setPeriod('soir')}
            >
              Soir
            </button>
          </div>
        </div>
      </div>

      {/* Categories editor */}
      <div className="est-menu-editor">
        {categories.map((cat, catIdx) => (
          <div key={catIdx} className="est-menu-category">
            <div className="est-menu-category-head">
              <input
                type="text"
                placeholder="Nom de la catégorie (ex: Plats, Entrées, Desserts...)"
                value={cat.libelle}
                onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                className="est-menu-cat-input"
              />
              {categories.length > 1 && (
                <button
                  type="button"
                  className="est-menu-remove"
                  onClick={() => removeCategory(catIdx)}
                  title="Supprimer la catégorie"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>

            <div className="est-menu-plats">
              {cat.plats.map((plat, platIdx) => (
                <div key={platIdx} className="est-menu-plat-row">
                  <UtensilsCrossed size={14} className="est-menu-plat-icon" />
                  <input
                    type="text"
                    placeholder="Nom du plat..."
                    value={plat.libelle}
                    onChange={(e) => updatePlat(catIdx, platIdx, e.target.value)}
                    className="est-menu-plat-input"
                  />
                  {cat.plats.length > 1 && (
                    <button
                      type="button"
                      className="est-menu-remove-small"
                      onClick={() => removePlat(catIdx, platIdx)}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="est-menu-add-plat"
                onClick={() => addPlat(catIdx)}
              >
                <Plus size={14} /> Ajouter un plat
              </button>
            </div>
          </div>
        ))}

        <button type="button" className="est-btn est-btn-ghost" onClick={addCategory}>
          <Plus size={16} /> Ajouter une catégorie
        </button>
      </div>

      {feedback && (
        <div className={`est-alert ${feedback.kind === 'ok' ? 'est-alert-ok' : 'est-alert-error'}`}>
          {feedback.kind === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{feedback.text}</span>
        </div>
      )}

      <div className="est-actions">
        <button
          type="button"
          className="est-btn est-btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'En cours...' : 'Publier le menu'}
        </button>
        {existingSource === 'crous' && (
          <button
            type="button"
            className="est-btn est-btn-ghost"
            onClick={handleClear}
            disabled={submitting}
          >
            <Trash2 size={16} /> Supprimer (revenir à l'API)
          </button>
        )}
      </div>
    </div>
  );
}

export default MenuPanel;
